import { useState } from 'react';
import { UserPlus, Pencil, Trash2, ShieldCheck } from 'lucide-react';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useUsers, useSaveUser, useDeleteUser, useMe, type AppUser } from '@/api/hooks';
import { ApiError } from '@/api/client';
import { Field, EMAIL_RE, Warn } from '@/components/FormKit';

const ROLES = ['Viewer', 'Contributor', 'Approver', 'Admin'];
const BLANK = { name: '', email: '', role: 'Contributor' };

export function Users() {
  const notify = useToast();
  const { data: me } = useMe();
  const q = useUsers();
  const save = useSaveUser();
  const del = useDeleteUser();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [form, setForm] = useState<{ name: string; email: string; role: string }>(BLANK);
  const [toDelete, setToDelete] = useState<AppUser | null>(null);
  const [search, setSearch] = useState('');

  function openNew() {
    setEditing(null);
    setForm(BLANK);
    setShowForm(true);
  }
  function openEdit(u: AppUser) {
    setEditing(u);
    setForm({ name: u.Name, email: u.EMail, role: u.Role ?? 'Contributor' });
    setShowForm(true);
  }
  const set = (k: keyof typeof form, v: string) => setForm((s) => ({ ...s, [k]: v }));

  const emailBad = !!form.email && !EMAIL_RE.test(form.email);
  const invalid = !form.name.trim() || !form.email.trim() || emailBad || !form.role;

  async function submit() {
    try {
      await save.mutateAsync({ originalEmail: editing?.EMail, body: { ...form, email: form.email.trim() } });
      notify('success', editing ? 'User updated.' : 'User added.');
      setShowForm(false);
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Save failed');
    }
  }

  async function confirmDelete() {
    const u = toDelete;
    setToDelete(null);
    if (!u) return;
    try {
      await del.mutateAsync(u.EMail);
      notify('success', 'User removed.');
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Delete failed');
    }
  }

  const users = q.data ?? [];
  // Live client-side filter — the whole list is already loaded, so no need for a
  // Search button; matches name, email, or role (case-insensitive).
  const term = search.trim().toLowerCase();
  const filtered = term
    ? users.filter(
        (u) =>
          u.Name.toLowerCase().includes(term) ||
          u.EMail.toLowerCase().includes(term) ||
          (u.Role ?? '').toLowerCase().includes(term),
      )
    : users;

  return (
    <div className="w-full space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-primary">
            <ShieldCheck className="h-5 w-5" /> Manage Users
          </h2>
          <p className="text-xs text-muted-foreground">
            Controls in-app access (the <code>tb_Users</code> table). Admins can approve/submit; other roles are read/contribute.
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={openNew}>
          <UserPlus className="h-4 w-4" /> Add User
        </button>
      </div>

      {showForm && (
        <div className="card max-w-3xl space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-primary">
            {editing ? `Edit ${editing.Name}` : 'Add User'}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Name *">
              <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" />
            </Field>
            <Field label={<>Email * <Warn show={emailBad} tip="Enter a valid email" /></>}>
              <input className="input" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="user@brccpa.com" />
            </Field>
            <Field label="Role *">
              <select className="input" value={form.role} onChange={(e) => set('role', e.target.value)}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" disabled={invalid || save.isPending} onClick={submit}>
              {editing ? 'Save changes' : 'Add user'}
            </button>
            <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          className="input max-w-sm"
          placeholder="Search name, email, or role…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="btn-secondary" onClick={() => setSearch('')}>Clear</button>
        )}
        <span className="text-xs text-muted-foreground">
          {filtered.length} of {users.length}
        </span>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2.5 font-semibold">Name</th>
              <th className="px-4 py-2.5 font-semibold">Email</th>
              <th className="px-4 py-2.5 font-semibold">Role</th>
              <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {q.isFetching && !users.length ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : !users.length ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No users.</td></tr>
            ) : !filtered.length ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No users match “{search}”.</td></tr>
            ) : (
              filtered.map((u) => {
                const isSelf = me?.email?.toLowerCase() === u.EMail.toLowerCase();
                return (
                  <tr key={u.EMail} className="border-b border-border/60 hover:bg-secondary/20">
                    <td className="px-4 py-2.5 font-medium">{u.Name}</td>
                    <td className="px-4 py-2.5">{u.EMail}</td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        (u.Role ?? '').toLowerCase() === 'admin'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-secondary text-secondary-foreground'
                      }`}>{u.Role || 'Viewer'}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary" title="Edit" onClick={() => openEdit(u)}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
                          title={isSelf ? "You can't remove yourself" : 'Remove'}
                          disabled={isSelf}
                          onClick={() => setToDelete(u)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!toDelete}
        message={`Remove ${toDelete?.Name} (${toDelete?.EMail}) from app access?`}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
